import { ofetch } from 'ofetch'
import { useAuthStore } from '@/stores/auth'
import { withTenantSchema } from '@/utils/apiHeaders'
import { getErrorMessage, logError, normalizeError } from '@/utils/errors'

function normalizeHeaders(input: HeadersInit | undefined): Record<string, string> {
  const headers: Record<string, string> = {}

  if (input instanceof Headers) {
    input.forEach((value, key) => {
      headers[key] = value
    })
  } else if (typeof input === 'object' && input !== null) {
    Object.assign(headers, input)
  }

  return headers
}

export function useApiFetch() {
  return ofetch.create({
    baseURL: `${import.meta.env.VITE_API_BASE_URL}/api/${import.meta.env.VITE_API_VERSION}/`,

    onRequest: ({ options }) => {
      const authStore = useAuthStore()
      const token = authStore.token
      const headers = normalizeHeaders(options.headers)

      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      headers['Accept'] = 'application/json'
      headers['X-Requested-With'] = 'XMLHttpRequest'

      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json'
      } else if ('Content-Type' in headers) {
        delete headers['Content-Type']
      }

      options.headers = new Headers(withTenantSchema(headers))
    },

    onResponse: ({ response }) => {
      if (window.trackCustomEvent) {
        window.trackCustomEvent('API', 'Success', {
          url: response.url,
          status: response.status,
          method: (response as any).request?.method || (response as any).config?.method || 'GET',
        })
      }
    },

    onResponseError: ({ response, request }) => {
      const authStore = useAuthStore()
      const normalizedError = normalizeError(response, `API Error: ${response.status}`)

      if (response.status === 401) {
        authStore.logout()
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }

      if (window.trackError) {
        window.trackError(new Error(getErrorMessage(response, `API Error: ${response.status}`)), {
          url: response.url,
          status: response.status,
          method: request instanceof Request ? request.method : 'GET',
          responseText: normalizedError.message,
        })
      }

      logError('API Error', response, {
        url: response.url,
        method: request instanceof Request ? request.method : 'GET',
      })
    },

    timeout: 30000,
    retry: 2,
    retryDelay: 1000,
  })
}

export function useAuthenticatedFetch() {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    throw new Error('User must be authenticated to use this API')
  }

  return useApiFetch()
}

export function usePublicFetch() {
  return ofetch.create({
    baseURL: `${import.meta.env.VITE_API_BASE_URL}/api/${import.meta.env.VITE_API_VERSION}/`,

    onRequest: ({ options }) => {
      const headers = normalizeHeaders(options.headers)
      headers['Accept'] = 'application/json'

      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json'
      } else if ('Content-Type' in headers) {
        delete headers['Content-Type']
      }

      options.headers = new Headers(withTenantSchema(headers))
    },

    onResponseError: ({ response, request }) => {
      if (window.trackError) {
        window.trackError(new Error(getErrorMessage(response, `Public API Error: ${response.status}`)), {
          url: response.url,
          status: response.status,
          method: request instanceof Request ? request.method : 'GET',
        })
      }

      logError('Public API Error', response, {
        url: response.url,
        method: request instanceof Request ? request.method : 'GET',
      })
    },

    timeout: 30000,
    retry: 1,
  })
}
